/**
 * Sidebar — desktop left rail & mobile sheet body.
 *
 * A single component used in two contexts (fixed column on >= 1024px, inside
 * a sheet drawer below that). The parent controls visibility; this component
 * only renders the nav.
 */

import { NavLink } from 'react-router-dom';

import { navItems } from '@/lib/nav-config';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

export interface SidebarProps {
  /** Called when a nav item is clicked — used to close the mobile drawer. */
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const role = useAuthStore((s) => s.user?.role);
  const visibleItems = navItems.filter((item) => {
    if (item.requiresRole && role !== item.requiresRole) {
      return false;
    }
    return true;
  });
  return (
    <aside
      aria-label="Navegação principal"
      className="flex h-full w-full flex-col border-r bg-card"
    >
      <div className="flex h-16 items-center border-b px-6">
        <span className="text-lg font-semibold tracking-tight">Sales Agent</span>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  'hover:bg-accent hover:text-accent-foreground',
                  isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground',
                )
              }
              end={item.to === '/dashboard'}
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{item.label}</span>
              {!item.implemented && (
                <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                  soon
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
